import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { Wordmark } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import InstallButton from "@/components/InstallButton";
import ThemeToggle from "@/components/ThemeToggle";

const LINKS = [
  { label: "Next Friday", to: "/#next" },
  { label: "Your first Friday", to: "/#first-friday" },
  { label: "Venues", to: "/#venues" },
  { label: "Topics", to: "/#topics" },
  { label: "Events", to: "/#events" },
];

export default function PublicHeader() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const cta = user ? (
    <Button asChild data-testid="header-portal-button" className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]">
      <Link to="/portal">Go to portal</Link>
    </Button>
  ) : (
    <div className="flex items-center gap-2">
      <Button asChild variant="ghost" data-testid="header-login-button">
        <Link to="/login">Sign in</Link>
      </Button>
      <Button asChild data-testid="header-join-button" className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]">
        <Link to="/register">Join the group</Link>
      </Button>
    </div>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/75" data-testid="public-header">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Wordmark withTagline />
        <nav className="hidden items-center gap-1 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.to}
              className="rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-2 md:flex">
          <ThemeToggle />
          <InstallButton variant="ghost" />
          {cta}
        </div>
        <div className="flex items-center gap-1 md:hidden">
          <ThemeToggle />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" data-testid="header-mobile-menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-card">
              <div className="mt-6 flex flex-col gap-1">
                {LINKS.map((l) => (
                  <a
                    key={l.label}
                    href={l.to}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground hover:bg-secondary"
                  >
                    {l.label}
                  </a>
                ))}
                <div className="mt-4 flex flex-col gap-2">{cta}</div>
                <InstallButton className="mt-2 w-full" />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
